import React, { useRef, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { gsap } from 'gsap';
import {
  faFacebookF, faXTwitter, faSoundcloud,
  faYoutube, faInstagram, faSpotify,
} from '@fortawesome/free-brands-svg-icons';
import InfoBlock from './InfoBlock';
import { formatDateParts } from '../utils/formatters';
import styles from '../styles/components/_ArtistModalContent.module.scss';
import Loader from './Loader';

const getSocialIcon = (name) => ({
  facebook: faFacebookF,
  twitter: faXTwitter,
  soundcloud: faSoundcloud,
  youtube: faYoutube,
  spotify: faSpotify,
  instagram: faInstagram
}[name?.toLowerCase()]);

const decodeHtmlEntities = (text) => {
  if (!text) return '';
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

const ArtistModalContent = ({ selectedData, onClose, onTicketClick }) => {
  const { artist, event } = selectedData || {};
  const [isLoading, setIsLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const timelineRef = useRef(null);

  const contentRefs = {
    hero: useRef(null),
    info: useRef(null),
    bio: useRef(null),
    socials: useRef(null),
    spotify: useRef(null)
  };

  // Animation de sortie
  const handleExit = () => {
    if (timelineRef.current) {
      timelineRef.current.kill();
    }
    
    const tl = gsap.timeline({
      onComplete: () => {
        onClose();
      }
    });
    
    // Animation de sortie des éléments dans l'ordre inverse
    const elementsToAnimate = [
      contentRefs.bio.current, 
      contentRefs.socials.current, 
      contentRefs.info.current
    ].filter(el => el !== null);
    
    if (elementsToAnimate.length > 0) {
      tl.to(elementsToAnimate, {
        opacity: 0,
        y: 20,
        duration: 0.3,
        stagger: 0.1,
        ease: "power2.inOut"
      });
    }
    
    // Animation de sortie de l'image
    if (imageRef.current) {
      tl.to(imageRef.current, {
        scale: 1.2,
        opacity: 0,
        duration: 0.8,
        ease: "power2.inOut"
      }, elementsToAnimate.length > 0 ? "-=0.2" : 0);
    }
    
    // Animation de sortie du conteneur
    if (containerRef.current) {
      tl.to(containerRef.current, {
        opacity: 0,
        duration: 0.3,
        ease: "power2.inOut"
      }, "-=0.3");
    }
    
    timelineRef.current = tl;
  };

  // Effet pour initialiser les animations une fois que le composant est monté
  useEffect(() => {
    if (artist && event) {
      setIsLoading(false);
      
      // Marquer comme visible pour afficher le contenu
      setIsVisible(true);
      
      // Attendre que le DOM soit complètement rendu
      const timer = setTimeout(() => {
        setIsReady(true);
      }, 50);
      
      return () => clearTimeout(timer);
    }
  }, [artist, event]);
  
  // Effet pour gérer les animations une fois que le composant est prêt
  useEffect(() => {
    if (!isReady) return;
    
    if (timelineRef.current) {
      timelineRef.current.kill();
    }
    
    const tl = gsap.timeline();
    timelineRef.current = tl;
    
    // Animation d'entrée du conteneur
    if (containerRef.current) {
      tl.fromTo(containerRef.current, 
        { opacity: 0 },
        { opacity: 1, duration: 0.3, ease: "power2.out" }
      );
    }
    
    // Animation d'entrée de l'image hero
    if (imageRef.current) {
      tl.fromTo(imageRef.current,
        { scale: 1.2, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.8, ease: "power2.inOut" }
      );
    }
    
    // Animation d'entrée des éléments de contenu
    const contentElements = [
      contentRefs.info.current, 
      contentRefs.bio.current, 
      contentRefs.socials.current,
      contentRefs.spotify.current
    ].filter(el => el !== null);
    
    if (contentElements.length > 0) {
      tl.fromTo(contentElements,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, stagger: 0.15, ease: "power2.out" },
        "-=0.6"
      );
    }
    
    return () => {
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
    };
  }, [isReady]);

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader type="rings" />
      </div>
    );
  }

  if (!event) {
    return <div className={styles.error}>Données indisponibles</div>;
  }

  const artistName = decodeHtmlEntities(artist?.title?.rendered || event.title);
  const socialLinks = artist?.acf?.reseaux_sociaux || [];
  const dateParts = formatDateParts(event?.date || new Date());
  const dateFinParts = event?.date_fin ? formatDateParts(event.date_fin) : null;
  const typePerformance = artist?.acf?.type_performance || '';
  const supportArtists = event?.artistesSupport?.map(a => a.name).join(', ') || '';
  const biographie = artist?.acf?.biographie || event.description || '';
  const additionalArtists = event?.artiste_add?.map(a => a.name) || [];

  return (
    <div 
      className={`${styles.container} ${isVisible ? styles.visible : ''}`} 
      ref={containerRef}
      style={{ opacity: 0 }}
    >
      <div
        className={styles.heroSection}
        ref={contentRefs.hero}
      >
        <div className={styles.imageWrapper}>
          <div className={styles.imageContainer}>
            <img
              ref={imageRef}
              src={artist?.acf?.photo?.[0]?.url || event.mainImage}
              alt={artistName}
              className={styles.heroImage}
              loading="eager"
              style={{ opacity: 0, transform: 'scale(1.2)' }}
            />
          </div>
        </div>
      </div>

      <div className={styles.content}>
        <div ref={contentRefs.info} className={styles.infoBlockWrapper} style={{ opacity: 0, transform: 'translateY(20px)' }}>
          <InfoBlock
            dateParts={dateParts}
            dateFinParts={dateFinParts}
            typePerformance={typePerformance}
            title={artistName}
            titleTag="h1"
            theme="dark"
            subtitle={supportArtists}
            showVerticalLabel={false}
            additionalArtists={additionalArtists}
            addInfosEvent={event?.add_infos_event}
            variant="modal"
          />
        </div>

        {socialLinks.length > 0 && (
          <div ref={contentRefs.socials} className={styles.socialLinks} style={{ opacity: 0, transform: 'translateY(20px)' }}>
            {socialLinks.map((social, i) => (
              <a
                key={i}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialIcon}
              >
                <FontAwesomeIcon icon={getSocialIcon(social.nom)} />
              </a>
            ))}
          </div>
        )}

        <div className={styles.blockWrapper}>
          <div
            ref={contentRefs.bio}
            className={styles.biography}
            dangerouslySetInnerHTML={{ __html: biographie }}
            style={{ opacity: 0, transform: 'translateY(20px)' }}
          />

          {artist?.acf?.spotify_url && (
            <div 
              ref={contentRefs.spotify} 
              className={styles.spotifyPlayer} 
              style={{ opacity: 0, transform: 'translateY(20px)' }}
            >
              <iframe
                src={`https://open.spotify.com/embed/artist/${artist.acf.spotify_url}?utm_source=generator`}
                frameBorder="0"
                allowFullScreen
                width="100%" 
                height="152px"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                title={`Lecteur Spotify pour ${artistName}`}
              />
            </div>
          )}
        </div>

        <div className={styles.buttonContainer}>
          <button
            className="custom-button custom-button--light"
            onClick={onTicketClick}
          >
            Ticket
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArtistModalContent;
